import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import { GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid-premium';
import { Table } from '../../components/Table';
import { TableRef } from '../../components/Table/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { fetchReconciliationRuns } from '../../redux/modules/reconciliation/reconciliation.actions';
import { reconciliationSelector } from '../../redux/modules/reconciliation/reconciliation.selectors';
import { ReconciliationPhase, ReconciliationRun } from '../../types/reconciliation';
import { ReconciliationItemComparisonView } from './ReconciliationItemComparisonView';
import { ReconciliationRunDetailView } from './ReconciliationRunDetailView';

function parseRunDate(row: ReconciliationRun): Date | null {
  const raw = row.run_date ?? row.createdAt;
  if (raw == null || raw === '') {
    return null;
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Query keys for reconciliation deep links (also cleared when selecting the Reconciliation Runs tab). */
export const RECONCILIATION_RUN_ID_PARAM = 'reconciliationRunId';
export const RECONCILIATION_ITEM_ID_PARAM = 'reconciliationItemId';

const TAB_PARAM_RECONCILIATION = 'reconciliationRuns';

type ReconciliationHrefMode = 'list' | { runId: number } | { runId: number; itemId: number };

function buildReconciliationHref(pathname: string, base: URLSearchParams, mode: ReconciliationHrefMode): string {
  const p = new URLSearchParams(base);
  p.set('tab', TAB_PARAM_RECONCILIATION);
  if (mode === 'list') {
    p.delete(RECONCILIATION_RUN_ID_PARAM);
    p.delete(RECONCILIATION_ITEM_ID_PARAM);
  } else if ('itemId' in mode) {
    p.set(RECONCILIATION_RUN_ID_PARAM, String(mode.runId));
    p.set(RECONCILIATION_ITEM_ID_PARAM, String(mode.itemId));
  } else {
    p.set(RECONCILIATION_RUN_ID_PARAM, String(mode.runId));
    p.delete(RECONCILIATION_ITEM_ID_PARAM);
  }
  return `${pathname}?${p.toString()}`;
}

function ReconciliationBreadcrumbs({
  pathname,
  searchParams,
  mode,
}: {
  pathname: string;
  searchParams: URLSearchParams;
  mode: ReconciliationHrefMode;
}) {
  const listHref = buildReconciliationHref(pathname, searchParams, 'list');

  if (mode === 'list') {
    return (
      <Breadcrumbs sx={{ mb: 2 }} aria-label="Reconciliation navigation">
        <Typography color="text.primary">Reconciliation runs</Typography>
      </Breadcrumbs>
    );
  }

  if ('itemId' in mode) {
    const runHref = buildReconciliationHref(pathname, searchParams, { runId: mode.runId });
    return (
      <Breadcrumbs sx={{ mb: 2 }} aria-label="Reconciliation navigation">
        <MuiLink component={RouterLink} to={listHref} underline="hover" color="inherit">
          Reconciliation runs
        </MuiLink>
        <MuiLink component={RouterLink} to={runHref} underline="hover" color="inherit">
          Run {mode.runId}
        </MuiLink>
        <Typography color="text.primary">Item {mode.itemId}</Typography>
      </Breadcrumbs>
    );
  }

  return (
    <Breadcrumbs sx={{ mb: 2 }} aria-label="Reconciliation navigation">
      <MuiLink component={RouterLink} to={listHref} underline="hover" color="inherit">
        Reconciliation runs
      </MuiLink>
      <Typography color="text.primary">Run {mode.runId}</Typography>
    </Breadcrumbs>
  );
}

function parseReconciliationRunIdParam(raw: string | null): number | null {
  if (raw == null || raw === '') {
    return null;
  }
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1) {
    return null;
  }
  return n;
}

function parseReconciliationItemIdParam(raw: string | null): number | null {
  if (raw == null || raw === '') {
    return null;
  }
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1) {
    return null;
  }
  return n;
}

interface ReconciliationRunsProps {
  orderId: string;
}

function ReconciliationRuns({ orderId }: ReconciliationRunsProps) {
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRunId = parseReconciliationRunIdParam(searchParams.get(RECONCILIATION_RUN_ID_PARAM));
  const selectedItemId = parseReconciliationItemIdParam(searchParams.get(RECONCILIATION_ITEM_ID_PARAM));

  const openRunDetail = (id: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(RECONCILIATION_RUN_ID_PARAM, String(id));
        next.delete(RECONCILIATION_ITEM_ID_PARAM);
        return next;
      },
      { replace: true },
    );
  };

  const closeRunDetail = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(RECONCILIATION_RUN_ID_PARAM);
        next.delete(RECONCILIATION_ITEM_ID_PARAM);
        return next;
      },
      { replace: true },
    );
  };

  const openItemComparison = (itemId: number) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(RECONCILIATION_ITEM_ID_PARAM, String(itemId));
        return next;
      },
      { replace: true },
    );
  };

  const closeItemComparison = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(RECONCILIATION_ITEM_ID_PARAM);
        return next;
      },
      { replace: true },
    );
  };

  const { reconciliationRuns, totalReconciliationRuns, fetchReconciliationRunsStatus } =
    useSelector(reconciliationSelector);
  const tableRef = useRef<TableRef>(null);
  const [columns] = useState<GridColDef<ReconciliationRun>[]>([
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      disableExport: true,
      width: 88,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<ReconciliationRun>) => (
        <IconButton
          aria-label="Open run details"
          size="small"
          onClick={() => openRunDetail(params.row.id)}
          color="inherit"
        >
          <OpenInNewIcon fontSize="small" />
        </IconButton>
      ),
    },
    {
      field: 'id',
      headerName: 'Run Id',
      width: 120,
      type: 'number',
    },
    {
      field: 'run_date',
      headerName: 'Run Date',
      type: 'date',
      minWidth: 180,
      valueGetter: (params: GridValueGetterParams<ReconciliationRun>) => parseRunDate(params.row),
      renderCell: (params: GridRenderCellParams<ReconciliationRun>) => {
        if (params.value == null) {
          return '';
        }

        return params.formattedValue;
      },
    },
    {
      field: 'run_type',
      headerName: 'Run Type',
      type: 'string',
      width: 200,
      valueGetter: (params: GridValueGetterParams<ReconciliationRun>) =>
        params.row.run_type ?? params.row.runType ?? '',
    },
    {
      field: 'phase',
      headerName: 'Phase',
      type: 'singleSelect',
      valueOptions: Object.values(ReconciliationPhase),
      width: 220,
      valueGetter: (params: GridValueGetterParams<ReconciliationRun>) => params.row.phase ?? '',
    },
    {
      field: 'status',
      headerName: 'Status',
      type: 'string',
      width: 160,
    },
  ]);

  if (selectedRunId != null && selectedItemId != null) {
    return (
      <Box sx={{ width: '100%' }}>
        <ReconciliationBreadcrumbs
          pathname={pathname}
          searchParams={searchParams}
          mode={{ runId: selectedRunId, itemId: selectedItemId }}
        />
        <ReconciliationItemComparisonView runId={selectedRunId} itemId={selectedItemId} onBack={closeItemComparison} />
      </Box>
    );
  }

  if (selectedRunId != null) {
    return (
      <Box sx={{ width: '100%' }}>
        <ReconciliationBreadcrumbs pathname={pathname} searchParams={searchParams} mode={{ runId: selectedRunId }} />
        <ReconciliationRunDetailView
          runId={selectedRunId}
          onBack={closeRunDetail}
          onViewComparison={openItemComparison}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <ReconciliationBreadcrumbs pathname={pathname} searchParams={searchParams} mode="list" />
      <Table
        ref={tableRef}
        fetchItems={fetchReconciliationRuns}
        fetchItemsCustomParams={{ orderId }}
        rows={reconciliationRuns}
        columns={columns}
        loading={fetchReconciliationRunsStatus === fetchStatuses.pending}
        rowCount={totalReconciliationRuns}
        initialPageSize={50}
        toolbarExportProps={{
          csvOptions: {
            fileName: `Reconciliation_Runs_${new Date().toISOString().split('T')[0]}`,
          },
          excelOptions: {
            fileName: `Reconciliation_Runs_${new Date().toISOString().split('T')[0]}`,
          },
        }}
        getRowId={(row) => String(row.id)}
      />
    </Box>
  );
}

export default ReconciliationRuns;
